import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCubes as faSugar,
    faDroplet as faMilk,
    faFilter as faBrewType,
    faCube,
} from '@fortawesome/free-solid-svg-icons';
import { getMilkColor, getSugarColor } from '../utils/colors';
import type { SupermarketFormat } from '../types';

interface FormatChipsProps {
    format?: { [key: string]: string } | null;
    // supermarket ratings carry a structured {milk,sugar,iced} format instead of the
    // free-form shop/home format map. When present we render emoji chips for the true
    // flags only (legacy flavor-only supermarket ratings pass nothing and render none).
    supermarketFormat?: SupermarketFormat | null;
}

// emoji chip labels for the supermarket format flags, in display order.
const SUPERMARKET_FLAGS: ReadonlyArray<{ key: keyof SupermarketFormat; emoji: string; label: string }> = [
    { key: 'milk', emoji: '🥛', label: 'milk' },
    { key: 'sugar', emoji: '🍬', label: 'sugar' },
    { key: 'iced', emoji: '🧊', label: 'iced' },
];

export const FormatChips: React.FC<FormatChipsProps> = ({ format, supermarketFormat }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    // supermarket branch — structured flags, independent of the legacy format map.
    if (supermarketFormat) {
        const active = SUPERMARKET_FLAGS.filter(({ key }) => supermarketFormat[key]);
        if (active.length === 0) return null;
        return (
            <div className='format-display'>
                {active.map(({ key, emoji, label }) => (
                    <div key={key} className='format-icon format-icon--supermarket'>
                        <span className='format-emoji' aria-hidden>{emoji}</span>
                        <span className='format-value'>{label}</span>
                    </div>
                ))}
            </div>
        );
    }

    if (!format) return null;

    const tooltipDetails = Object.entries(format)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${k.replace(/-/g, ' ')}: ${v}`);

    const sugarLevel = format['sugar-level'];
    const sugarType = format['sugar-type'];
    const milkType = format['milk-type'];
    const brewType = format['brew-type'];

    const hasBrew = !!brewType;
    const hasSugar = !!sugarLevel && sugarLevel !== 'no-sugar';
    const hasMilk = !!milkType && milkType !== 'no-milk';
    if (!hasBrew && !hasSugar && !hasMilk) return null;

    const onEnter = () => setShowTooltip(true);
    const onLeave = () => setShowTooltip(false);

    return (
        <div className='format-display'>
            {brewType && (
                <div className='format-icon' onMouseEnter={onEnter} onMouseLeave={onLeave}>
                    <FontAwesomeIcon icon={faBrewType} style={{ color: '#4a5568' }} />
                    <span className='format-value'>{brewType}</span>
                </div>
            )}
            {sugarLevel && sugarLevel !== 'no-sugar' && (
                <div className='format-icon' onMouseEnter={onEnter} onMouseLeave={onLeave}>
                    <FontAwesomeIcon
                        icon={sugarLevel === 'a-little' ? faCube : faSugar}
                        className='sugar-icon'
                        style={{ color: getSugarColor(sugarLevel, sugarType) }}
                    />
                    <span className='format-value'>{sugarLevel}</span>
                </div>
            )}
            {milkType && milkType !== 'no-milk' && (
                <div className='format-icon' onMouseEnter={onEnter} onMouseLeave={onLeave}>
                    <FontAwesomeIcon
                        icon={faMilk}
                        className='milk-icon'
                        style={{ color: getMilkColor(milkType) }}
                    />
                    <span className='format-value'>{milkType}</span>
                </div>
            )}
            {showTooltip && tooltipDetails.length > 0 && (
                <div className='format-tooltip'>
                    {tooltipDetails.map((detail, i) => {
                        const [label, value] = detail.split(': ');
                        return (
                            <div key={i} className='tooltip-row'>
                                <span className='tooltip-label'>{label}:</span>
                                <span className='tooltip-value'>{value}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
